import { inject } from '@angular/core'

import { lastValueFrom } from 'rxjs'

import { FlxModalService } from '@flowx/angular-ui-toolkit'
import {
  ExportBinariesComponent,
  ExportModalData,
} from '@flowx/designer/modules/projects/components/export-binaries/export-binaries.component'
import { ApplicationApi } from '@flowx/designer/shared/api/application.api'
import { ExportType } from '@flowx/designer/shared/entities/application.entity'
import { downloadHttpResponse } from '@flowx/shared/utils'

import { CHANGE_TARGET_VERSION_MODAL_CONFIG, injectExportReview } from './export-review'
import { injectWithErrorToast } from './with-error-toast'

import { ExportReviewOption } from '../export-review-modal/export-review-modal.component'
import {
  ExtendedNotification,
  NotificationActionHandler,
  NotificationActionType,
  NotificationType,
} from '../notifications.model'

type AppVersionExportActionType =
  | NotificationActionType.RETRY_EXPORT
  | NotificationActionType.REVIEW_EXPORT
  | NotificationActionType.DOWNLOAD_EXPORT

type AppVersionExportNotification = Extract<
  ExtendedNotification,
  { type: NotificationType.APP_VERSION_EXPORT }
>

/**
 * The actions an app version export notification offers. Must be called in an injection context: the
 * dependencies are resolved once, up front, so the handlers it returns stay safe to call after an
 * `await` or from inside a subscription. Each action runs in the workspace the export ran in, wherever
 * the user is now.
 */
export const injectAppVersionExportHandlers = (): Record<
  AppVersionExportActionType,
  NotificationActionHandler
> => {
  const applicationApi = inject(ApplicationApi)
  const modalService = inject(FlxModalService)
  const openExportReview = injectExportReview()
  const withErrorToast = injectWithErrorToast()

  // Re-runs straight away with the original parameters; the fresh run reports through a notification of its own.
  const retryExport = (notification: AppVersionExportNotification): Promise<void> =>
    withErrorToast('Failed to export version', () =>
      lastValueFrom(
        applicationApi.exportApplicationVersion(
          notification.appId,
          notification.appVersionId,
          {
            flowxVersion: notification.payload.flowxVersion,
            includeBinaries: notification.payload.includeBinaries,
            retry: true,
          },
          notification.workspaceId
        )
      )
    )

  const reviewExport = (notification: AppVersionExportNotification): void =>
    openExportReview(notification, {
      [ExportReviewOption.DOWNLOAD]: () => downloadExport(notification),
      [ExportReviewOption.CHANGE_TARGET_VERSION]: () => changeTargetVersion(notification),
    })

  const downloadExport = (notification: AppVersionExportNotification): Promise<void> =>
    withErrorToast('Failed to download version', async () => {
      const response = await lastValueFrom(
        applicationApi.downloadExport(
          notification.appId,
          notification.appVersionId,
          notification.payload.flowxVersion,
          notification.payload.includeBinaries,
          notification.workspaceId
        )
      )

      downloadHttpResponse(response, `${notification.appName ?? notification.appId}.zip`)
    })

  // The export modal loads its own target versions and runs the export or the download itself, so
  // all it needs is the version.
  const changeTargetVersion = (notification: AppVersionExportNotification): void => {
    modalService.show(ExportBinariesComponent, CHANGE_TARGET_VERSION_MODAL_CONFIG, {
      exportType: ExportType.VERSION,
      appVersion: {
        workspaceId: notification.workspaceId,
        appId: notification.appId,
        id: notification.appVersionId,
        appName: notification.appName,
        branchName: notification.branchName,
        branchOrder: notification.branchOrder,
        status: notification.appVersionStatus,
      },
    } as ExportModalData)
  }

  // Actions come from ACTION_BY_TYPE, so this always matches; the check is what lets the compiler
  // give each handler the export's payload without a cast.
  const forAppVersionExport =
    (run: (notification: AppVersionExportNotification) => void): NotificationActionHandler =>
    (notification) => {
      if (notification.type === NotificationType.APP_VERSION_EXPORT) {
        run(notification)
      }
    }

  return {
    [NotificationActionType.RETRY_EXPORT]: forAppVersionExport(retryExport),
    [NotificationActionType.REVIEW_EXPORT]: forAppVersionExport(reviewExport),
    [NotificationActionType.DOWNLOAD_EXPORT]: forAppVersionExport(downloadExport),
  }
}
